import { useState } from "react";
import {
  X, Bell, BellOff, Upload, Pencil, Trash2,
  ExternalLink, Download, Loader2, Users, Lock, HardHat,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import {
  useResourceDetail,
  useToggleSubscribe,
  useSoftDeleteResource,
} from "../hooks/useRepository";
import type { Resource, ResourceCategory } from "../types";
import { VersionHistoryList } from "./VersionHistoryList";
import { AccessConfigPanel } from "./AccessConfigPanel";
import { ContractorAccessPanel } from "./ContractorAccessPanel";
import { ResourceFormModal } from "./ResourceFormModal";
import { UploadVersionModal } from "./UploadVersionModal";
import { ResourceTypeBadge } from "./ResourceTypeBadge";
import { ManagersPanel } from "./ManagersPanel";
import { AccessGroupsModal } from "./AccessGroupsModal";

type Tab = "info" | "versions" | "access" | "managers";

interface Props {
  resource: Resource;
  categories: ResourceCategory[];
  onClose: () => void;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

export function ResourceDetailModal({ resource, categories, onClose }: Props) {
  const user = useAuthStore((s) => s.user);

  const { data: detail, isLoading } = useResourceDetail(resource.id);
  const { mutateAsync: toggleSub, isPending: toggling } = useToggleSubscribe();
  const { mutateAsync: softDelete, isPending: deleting } = useSoftDeleteResource();

  const [tab, setTab] = useState<Tab>("info");
  const [showEdit, setShowEdit] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showGroups, setShowGroups] = useState(false);

  // Full management = director or the owning category's coordinator.
  const canManage = detail?.can_manage ?? resource.can_manage;
  // The access tab is available to any coordinator/director who can view the resource
  // (the backend returns the access payload only for them).
  const canSeeAccess = detail?.access != null;
  const isManager = detail?.managers.some((m) => m.id === user?.id) ?? false;
  const canUpload = canManage || isManager;
  const isLink = resource.resource_type === "link";
  const isContractorDoc = resource.resource_type === "contractor_document";

  const tabs: { id: Tab; label: string }[] = [
    { id: "info", label: "Info" },
    ...(!isLink ? [{ id: "versions" as Tab, label: "Wersje" }] : []),
    ...(canSeeAccess ? [{ id: "access" as Tab, label: "Dostęp" }] : []),
    ...(canManage ? [{ id: "managers" as Tab, label: "Zarządzający" }] : []),
  ];

  async function handleDelete() {
    if (!confirm(`Usunąć zasób "${resource.title}"?`)) return;
    await softDelete(resource.id);
    onClose();
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
        <div
          className="bg-white dark:bg-gray-900 shadow-xl w-full sm:max-w-2xl flex flex-col rounded-t-2xl sm:rounded-xl max-h-[92dvh] sm:max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile drag handle */}
          <div className="sm:hidden flex justify-center pt-2.5 pb-0.5 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>

          {/* Header */}
          <div className="flex items-start gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <ResourceTypeBadge type={resource.resource_type} />
                <span className="text-xs text-gray-400">{resource.category_name}</span>
              </div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mt-1">
                {resource.title}
              </h2>
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Subscribe bell */}
              <button
                onClick={() => toggleSub({ id: resource.id, subscribed: resource.is_subscribed })}
                disabled={toggling}
                title={resource.is_subscribed ? "Wyłącz powiadomienia" : "Włącz powiadomienia"}
                className="p-1.5 rounded-lg text-gray-400 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/20 transition-colors"
              >
                {resource.is_subscribed
                  ? <Bell size={16} className="text-[var(--color-primary)]" />
                  : <BellOff size={16} />
                }
              </button>

              {canUpload && !isLink && (
                <button
                  onClick={() => setShowUpload(true)}
                  title="Wgraj nową wersję"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                >
                  <Upload size={16} />
                </button>
              )}

              {canManage && (
                <>
                  <button
                    onClick={() => setShowEdit(true)}
                    title="Edytuj"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    title="Usuń"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                </>
              )}

              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          {tabs.length > 1 && (
            <div className="flex border-b border-gray-100 dark:border-gray-700 px-4 sm:px-5">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`text-sm font-medium px-3 py-2.5 border-b-2 transition-colors ${
                    tab === t.id
                      ? "border-[var(--color-primary)] text-[var(--color-primary)] dark:text-blue-400"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {t.label === "Dostęp" && <Lock size={12} className="inline mr-1" />}
                  {t.label === "Zarządzający" && <Users size={12} className="inline mr-1" />}
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:p-5">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                {tab === "info" && (
                  <div className="flex flex-col gap-4">
                    {resource.description && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {resource.description}
                      </p>
                    )}

                    {isLink && resource.url && (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-2 text-sm text-[var(--color-primary)] dark:text-blue-400 hover:underline break-all"
                      >
                        <ExternalLink size={14} className="flex-shrink-0 mt-0.5" />
                        <span>{resource.url}</span>
                      </a>
                    )}

                    {!isLink && detail?.latest_version && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[var(--color-primary)] dark:text-blue-400 font-medium mb-0.5">
                            Aktualna wersja v{detail.latest_version.version_number}
                          </p>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                            {detail.latest_version.original_name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(detail.latest_version.created_at)}
                            {detail.latest_version.uploaded_by_name && ` · ${detail.latest_version.uploaded_by_name}`}
                          </p>
                        </div>
                        <a
                          href={detail.latest_version.url}
                          download={detail.latest_version.original_name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-xs rounded-lg hover:bg-[var(--color-primary-hover)]"
                        >
                          <Download size={12} />
                          Pobierz
                        </a>
                      </div>
                    )}

                    {!isLink && !detail?.latest_version && (
                      <p className="text-sm text-gray-400 italic">
                        Brak pliku. {canUpload && "Wgraj pierwszą wersję."}
                      </p>
                    )}

                    <div className="text-xs text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3">
                      Dodany przez {resource.created_by_name ?? "—"} · {formatDate(resource.created_at)}
                    </div>
                  </div>
                )}

                {tab === "versions" && detail && (
                  <VersionHistoryList versions={detail.versions} />
                )}

                {tab === "access" && detail?.access && (
                  <div className="flex flex-col gap-6">
                    <AccessConfigPanel
                      resourceId={resource.id}
                      access={detail.access}
                      canFull={canManage}
                      onManageGroups={() => setShowGroups(true)}
                    />
                    {canManage && isContractorDoc && detail.contractor_access && (
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <HardHat size={15} className="text-amber-600" />
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Dostęp podwykonawców
                          </p>
                        </div>
                        <ContractorAccessPanel
                          resourceId={resource.id}
                          access={detail.contractor_access}
                        />
                      </div>
                    )}
                  </div>
                )}

                {tab === "managers" && canManage && detail && (
                  <ManagersPanel resourceId={resource.id} managers={detail.managers} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <ResourceFormModal
          categories={categories}
          resource={resource}
          onClose={() => setShowEdit(false)}
        />
      )}

      {showUpload && (
        <UploadVersionModal
          resourceId={resource.id}
          resourceTitle={resource.title}
          onClose={() => setShowUpload(false)}
        />
      )}

      {showGroups && <AccessGroupsModal onClose={() => setShowGroups(false)} />}
    </>
  );
}
